import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Public pages - loaded immediately
import Home from "./pages/Home";
import Gaming from "./pages/Gaming";
import Office from "./pages/Office";
import ProductDetail from "./pages/ProductDetail";
import GamingDesksCategory from "./pages/GamingDesksCategory";
import Support from "./pages/Support";
import About from "./pages/About";
import Contact from "./pages/Contact";

// Admin pages - lazy loaded (only downloaded when visiting /admin/*)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminHomepage = lazy(() => import("./pages/admin/AdminHomepage"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductEdit = lazy(() => import("./pages/admin/AdminProductEdit"));
const AdminAbout = lazy(() => import("./pages/admin/AdminAbout"));
const AdminContact = lazy(() => import("./pages/admin/AdminContact"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminGaming = lazy(() => import("./pages/admin/AdminGaming"));
const AdminOffice = lazy(() => import("./pages/admin/AdminOffice"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminHeaderFooter = lazy(() => import("./pages/admin/AdminHeaderFooter"));

import ProtectedRoute from "./components/ProtectedRoute";
import { AdminLangProvider } from "./contexts/AdminLangContext";
import "./i18n";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const updateMeta = (property, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
  if (el) el.setAttribute("content", content);
};

const AdminLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    // Dynamic canonical tag
    const url = `https://www.viyero.com.tr${pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    axios.get(`${API}/api/public/content/contact`).then(res => {
      const d = res.data;
      if (!d || !Object.keys(d).length) return;
      if (d.og_image) {
        updateMeta("og:image", d.og_image);
        updateMeta("twitter:image", d.og_image);
      }
      if (d.og_title) {
        updateMeta("og:title", d.og_title);
        updateMeta("twitter:title", d.og_title);
        document.title = d.og_title;
      }
      if (d.og_description) {
        updateMeta("og:description", d.og_description);
        updateMeta("twitter:description", d.og_description);
        updateMeta("description", d.og_description);
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/gaming" element={<Gaming />} />
          <Route path="/gaming/desks" element={<GamingDesksCategory />} />
          <Route path="/office" element={<Office />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/support" element={<Support />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Admin Routes - lazy loaded, wrapped with Suspense */}
          <Route path="/admin/login" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><AdminLogin /></AdminLangProvider></Suspense>} />
          <Route path="/admin/dashboard" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminDashboard /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/homepage" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminHomepage /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/gaming" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminGaming /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/office" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminOffice /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/support" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminSupport /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/products" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminProducts /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/products/:id" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminProductEdit /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/about" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminAbout /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/contact" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminContact /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/settings" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminSettings /></ProtectedRoute></AdminLangProvider></Suspense>} />
          <Route path="/admin/header-footer" element={<Suspense fallback={<AdminLoader />}><AdminLangProvider><ProtectedRoute><AdminHeaderFooter /></ProtectedRoute></AdminLangProvider></Suspense>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
